import { Plus, Users, Package } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CampaignInfluencerBlock, type InfluencerBlockData } from '../CampaignInfluencerBlock';
import { RowList } from '../ui/ListRows';
import { FormBlock } from '../ui/FormBlock';
import { CampaignServiceBlock, type ServiceBlockData } from '../CampaignServiceBlock';
import type { Influencer } from '../../types/influencer';
import type { ServiceProvider } from '../../types/serviceProvider';

interface SortableInfluencerItemProps {
  block: InfluencerBlockData;
  availableInfluencers: Influencer[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onChange: (updatedBlock: InfluencerBlockData) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  canRemove: boolean;
  removeDisabledReason?: string;
}

function SortableInfluencerItem({
  block,
  availableInfluencers,
  isExpanded,
  onToggleExpand,
  onChange,
  onRemove,
  onDuplicate,
  canRemove,
  removeDisabledReason,
}: SortableInfluencerItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'relative z-10 opacity-60 shadow-lg ring-2 ring-accent-400 rounded-xl bg-white' : ''}
    >
      <CampaignInfluencerBlock
        block={block}
        availableInfluencers={availableInfluencers}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        onChange={onChange}
        onRemove={onRemove}
        onDuplicate={onDuplicate}
        canRemove={canRemove}
        removeDisabledReason={removeDisabledReason}
        dragHandleProps={{ attributes, listeners }}
      />
    </div>
  );
}

interface SortableServiceItemProps {
  block: ServiceBlockData;
  availableProviders: ServiceProvider[];
  isExpanded: boolean;
  onToggleExpand: () => void;
  onChange: (updatedBlock: ServiceBlockData) => void;
  onRemove: () => void;
  onDuplicate: () => void;
  canRemove: boolean;
  removeDisabledReason?: string;
}

function SortableServiceItem({
  block,
  availableProviders,
  isExpanded,
  onToggleExpand,
  onChange,
  onRemove,
  onDuplicate,
  canRemove,
  removeDisabledReason,
}: SortableServiceItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={isDragging ? 'relative z-10 opacity-60 shadow-lg ring-2 ring-accent-400 rounded-xl bg-white' : ''}
    >
      <CampaignServiceBlock
        block={block}
        availableProviders={availableProviders}
        isExpanded={isExpanded}
        onToggleExpand={onToggleExpand}
        onChange={onChange}
        onRemove={onRemove}
        onDuplicate={onDuplicate}
        canRemove={canRemove}
        removeDisabledReason={removeDisabledReason}
        dragHandleProps={{ attributes, listeners }}
      />
    </div>
  );
}

interface ContentTabProps {
  influencerBlocks: InfluencerBlockData[];
  serviceBlocks: ServiceBlockData[];
  availableInfluencers: Influencer[];
  availableServiceProviders: ServiceProvider[];
  expandedInfluencers: Set<string>;
  expandedServices: Set<string>;
  onAddInfluencer: () => void;
  onAddService: () => void;
  onToggleInfluencer: (id: string) => void;
  onToggleService: (id: string) => void;
  onInfluencerChange: (id: string, block: InfluencerBlockData) => void;
  onServiceChange: (id: string, block: ServiceBlockData) => void;
  onRemoveInfluencer: (id: string) => void;
  onRemoveService: (id: string) => void;
  onDuplicateInfluencer: (id: string) => void;
  onDuplicateService: (id: string) => void;
  onReorderInfluencers: (blocks: InfluencerBlockData[]) => void;
  onReorderServices: (blocks: ServiceBlockData[]) => void;
}

export function ContentTab({
  influencerBlocks,
  serviceBlocks,
  availableInfluencers,
  availableServiceProviders,
  expandedInfluencers,
  expandedServices,
  onAddInfluencer,
  onAddService,
  onToggleInfluencer,
  onToggleService,
  onInfluencerChange,
  onServiceChange,
  onRemoveInfluencer,
  onRemoveService,
  onDuplicateInfluencer,
  onDuplicateService,
  onReorderInfluencers,
  onReorderServices,
}: ContentTabProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleInfluencerDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = influencerBlocks.findIndex(b => b.id === active.id);
      const newIndex = influencerBlocks.findIndex(b => b.id === over.id);
      const reordered = arrayMove(influencerBlocks, oldIndex, newIndex);
      onReorderInfluencers(reordered);
    }
  };

  const handleServiceDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = serviceBlocks.findIndex(b => b.id === active.id);
      const newIndex = serviceBlocks.findIndex(b => b.id === over.id);
      const reordered = arrayMove(serviceBlocks, oldIndex, newIndex);
      onReorderServices(reordered);
    }
  };

  return (
    <div className="p-5 sm:p-6 space-y-6">
      {/* Campaign-level summary */}
      {influencerBlocks.length > 0 && (() => {
        const totalDeliverables = influencerBlocks.reduce((s, b) => s + b.deliverables.length, 0);
        const totalFees = influencerBlocks.reduce((s, b) => s + (b.agreed_fee || 0), 0);
        const totalCommissions = influencerBlocks.reduce((s, b) =>
          s + b.deliverables.reduce((ds, d) => ds + (d.commission || 0), 0), 0);
        const fmt = (v: number) => new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(v);

        return (
          <FormBlock title="Resumo da campanha">
            <div>
              {/* Campaign totals row */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
                {[
                  { label: 'Criadores', value: influencerBlocks.length },
                  { label: 'Conteúdos', value: totalDeliverables },
                  { label: 'Total Fees', value: fmt(totalFees), highlight: false },
                  { label: 'Total Comissões', value: fmt(totalCommissions), highlight: true },
                ].map(item => (
                  <div key={item.label} className="bg-gray-50 rounded-xl p-3 text-center">
                    <p className="text-xs text-gray-500 mb-1">{item.label}</p>
                    <p className={`font-semibold text-sm ${item.highlight ? 'text-accent-700' : 'text-gray-800'}`}>
                      {item.value}
                    </p>
                  </div>
                ))}
              </div>

              {/* Per-creator breakdown table */}
              <div className="overflow-x-auto rounded-lg border border-gray-100">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="text-left px-3 py-2 font-semibold text-gray-600">Criador</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-600">Fee Total</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-600">Conteúdos</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-600">Fee / Conteúdo</th>
                      <th className="text-right px-3 py-2 font-semibold text-gray-600 text-accent-700">Comissão</th>
                    </tr>
                  </thead>
                  <tbody>
                    {influencerBlocks.map((b) => {
                      const creatorCommission = b.deliverables.reduce((s, d) => s + (d.commission || 0), 0);
                      const feePerDeliverable = b.deliverables.length > 0 ? (b.agreed_fee || 0) / b.deliverables.length : 0;
                      return (
                        <tr key={b.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors">
                          <td className="px-3 py-2 font-medium text-gray-800 truncate max-w-[140px]">
                            {b.influencer_name || <span className="text-gray-400 italic">Sem nome</span>}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-700">{fmt(b.agreed_fee || 0)}</td>
                          <td className="px-3 py-2 text-right text-gray-600">{b.deliverables.length}</td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-600">{fmt(feePerDeliverable)}</td>
                          <td className="px-3 py-2 text-right tabular-nums font-semibold text-accent-700">{fmt(creatorCommission)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-50 border-t border-gray-200">
                      <td className="px-3 py-2 font-bold text-gray-700">Total</td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-gray-800">{fmt(totalFees)}</td>
                      <td className="px-3 py-2 text-right font-bold text-gray-700">{totalDeliverables}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-gray-500">—</td>
                      <td className="px-3 py-2 text-right tabular-nums font-bold text-accent-700">{fmt(totalCommissions)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </FormBlock>
        );
      })()}
      <FormBlock title={influencerBlocks.length > 0 ? `Criadores · ${influencerBlocks.length}` : 'Criadores'}>
        {influencerBlocks.length > 0 && (
          <div className="flex justify-end mb-3">
            <button type="button" onClick={onAddInfluencer} className="cm-btn-add">
              <Plus className="w-4 h-4" />
              Adicionar criador
            </button>
          </div>
        )}
        <div>
          {influencerBlocks.length === 0 ? (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 py-4 rounded-xl border border-dashed border-gray-300 text-sm text-gray-500">
              <span className="inline-flex items-center gap-2"><Users className="w-4 h-4 text-gray-400" /> Ainda sem criadores</span>
              <button type="button" onClick={onAddInfluencer} className="cm-btn-add">
                <Plus className="w-4 h-4" />
                Adicionar criador
              </button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleInfluencerDragEnd}
            >
              <SortableContext
                items={influencerBlocks.map(b => b.id)}
                strategy={verticalListSortingStrategy}
              >
                <RowList>
                  {influencerBlocks.map((block) => (
                    <SortableInfluencerItem
                      key={block.id}
                      block={block}
                      availableInfluencers={availableInfluencers}
                      isExpanded={expandedInfluencers.has(block.id)}
                      onToggleExpand={() => onToggleInfluencer(block.id)}
                      onChange={(updatedBlock) => onInfluencerChange(block.id, updatedBlock)}
                      onRemove={() => onRemoveInfluencer(block.id)}
                      onDuplicate={() => onDuplicateInfluencer(block.id)}
                      canRemove={influencerBlocks.length > 1 || !block.existing || serviceBlocks.length > 0}
                      removeDisabledReason={
                        block.existing && influencerBlocks.length === 1 && serviceBlocks.length === 0
                          ? 'Não é possível remover o único criador sem serviços na campanha'
                          : undefined
                      }
                    />
                  ))}
                </RowList>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </FormBlock>

      <FormBlock title={serviceBlocks.length > 0 ? `Serviços · ${serviceBlocks.length}` : 'Serviços'}>
        {serviceBlocks.length > 0 && (
          <div className="flex justify-end mb-3">
            <button type="button" onClick={onAddService} className="cm-btn-add">
              <Plus className="w-4 h-4" />
              Adicionar serviço
            </button>
          </div>
        )}
        <div>
          {serviceBlocks.length === 0 ? (
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 py-4 rounded-xl border border-dashed border-gray-300 text-sm text-gray-500">
              <span className="inline-flex items-center gap-2"><Package className="w-4 h-4 text-gray-400" /> Sem serviços <span className="text-xs text-gray-400">(opcional)</span></span>
              <button type="button" onClick={onAddService} className="cm-btn-add">
                <Plus className="w-4 h-4" />
                Adicionar serviço
              </button>
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleServiceDragEnd}
            >
              <SortableContext
                items={serviceBlocks.map(b => b.id)}
                strategy={verticalListSortingStrategy}
              >
                <RowList>
                  {serviceBlocks.map((block) => (
                    <SortableServiceItem
                      key={block.id}
                      block={block}
                      availableProviders={availableServiceProviders}
                      isExpanded={expandedServices.has(block.id)}
                      onToggleExpand={() => onToggleService(block.id)}
                      onChange={(updatedBlock) => onServiceChange(block.id, updatedBlock)}
                      onRemove={() => onRemoveService(block.id)}
                      onDuplicate={() => onDuplicateService(block.id)}
                      canRemove={!block.existing}
                      removeDisabledReason={
                        block.existing ? 'Não é possível remover um serviço já guardado na campanha' : undefined
                      }
                    />
                  ))}
                </RowList>
              </SortableContext>
            </DndContext>
          )}
        </div>
      </FormBlock>
    </div>
  );
}
